import type { StoredCardKind } from "./stored-card-template";
import type { TourCardsQuery } from "./tour-cards";

/**
 * The Tours accordion block — the Tours block's tours, as a row of photos:
 * one open and wide, the rest folded to a strip (design.md D109).
 *
 * IT ASKS THE SAME QUESTION. The list node stores `source`, `categories`,
 * `tours` and `limit` under the Tours block's names, and `contentRequestOf`
 * answers it with `tourCardsRequest`, as it does the Tours carousel's
 * (`tour-carousel.ts`). A grid, a carousel and an accordion with one query
 * are one read.
 *
 * The panel settings (`showDestination`, `facts`, `details`) are read with
 * the Tours block's readers (`tour-cards.ts`). Only the layout is its own.
 */
export const TOUR_ACCORDION_TYPE = "tour-accordion";

/**
 * Where the open tour's details sit. `overlay` writes them over the foot of
 * its photo; `split` puts them in a column beside the photo, which moves with
 * the open tour.
 */
export const TOUR_ACCORDION_LAYOUTS = ["overlay", "split"] as const;
export type TourAccordionLayout = (typeof TOUR_ACCORDION_LAYOUTS)[number];

/** A stored value this build does not know falls back to `overlay`. */
export const readTourAccordionLayout = (value: unknown): TourAccordionLayout =>
  TOUR_ACCORDION_LAYOUTS.find((layout) => layout === value) ?? "overlay";

export const DEFAULT_TOUR_ACCORDION_LIMIT = 4;

/**
 * The most tours one row shows. Past eight, the folded strips are too narrow
 * to carry their tour names. The query asks for no more
 * (`tourAccordionQuery`), and a hand-picked list longer than this is cut
 * when it is drawn (`tourAccordionTours`).
 */
export const MAX_TOUR_ACCORDION_TOURS = 8;

/**
 * The Tours block's query, asking for at most one row of tours. A stored
 * `limit` of 9 (the Tours block's field allows it) would otherwise load a
 * tour the row never draws, and carry it in the published page. Hand-picked
 * tours are not cut here: the picks that are drafts or another agency's are
 * dropped by the read, so cutting the ids first could leave fewer than eight.
 */
export const tourAccordionQuery = (query: TourCardsQuery): TourCardsQuery => ({
  ...query,
  limit: Math.min(query.limit, MAX_TOUR_ACCORDION_TOURS),
});

/** The tours a block draws: the answer, cut to what one row can hold. */
export const tourAccordionTours = <T>(tours: readonly T[]): readonly T[] =>
  tours.slice(0, MAX_TOUR_ACCORDION_TOURS);

/**
 * The Tours accordion's STORED CARD (design.md D97, D109): one tour kept in
 * the document, drawn once per tour, whose parts the operator can select and
 * style. Its root is the tour's `<details>`. How it is bound is the shared
 * stored-card rule (`stored-card-template.ts`, `TOUR_ACCORDION_CARD_KIND`).
 */
export const TOUR_ACCORDION_TEMPLATE_TYPE = "tour-accordion-card";
export const TOUR_ACCORDION_FIELD_TYPE = "tour-accordion-field";

/** The list settings a field draws with, copied onto each field by the bind. */
export const TOUR_ACCORDION_FIELD_SETTINGS = [
  "showDestination",
  "facts",
  "details",
] as const;

/** Every part of a block wears its key class (`instance-classes.ts`). */
const TOUR_ACCORDION_KEY_PREFIX = "tc-tacc-";

const classNamesOf = (
  classes: ReadonlyArray<string | { name: string }> | undefined,
): string[] =>
  (classes ?? []).map((entry) =>
    typeof entry === "string" ? entry : entry.name,
  );

/** The block's key class, which no other block on the page wears, or
 *  undefined for a block with none (an exported pattern body). */
export const tourAccordionKeyOf = (list: {
  classes?: ReadonlyArray<string | { name: string }>;
}): string | undefined =>
  classNamesOf(list.classes).find((name) =>
    name.startsWith(TOUR_ACCORDION_KEY_PREFIX),
  );

type Classed = { classes?: ReadonlyArray<string | { name: string }> };

/**
 * The `name` a block's `<details>` share, so opening one folds only its own
 * block's open tour: the block's key. The key is on the block's styled
 * parts, the stored tour among them, and not on the list, so the tour is
 * read too. Reading the list alone gave every block the type's name: a
 * second accordion on the page then folded the first one's open tour while
 * the browser parsed the page, and React's hydration found `open` gone.
 *
 * A block with no key (a document written without the editor) gets no name
 * at all, never a shared one, for the same reason. Its runtime still folds
 * the others when one opens (`tour-accordion-controller.ts`).
 */
export const tourAccordionGroupName = (
  list: Classed,
  tour?: Classed,
): string | undefined =>
  tourAccordionKeyOf(list) ?? (tour && tourAccordionKeyOf(tour));

/**
 * The Tours accordion's tour (design.md D109). Its root is a `<details>`:
 * every copy carries the block's group `name`, so opening one folds the
 * others, and only the first is `open`. The stored root is `open` (the
 * editor keeps it open to edit), so the others have it removed. At most
 * eight, as the code drawing (`tourAccordionTours`).
 */
export const TOUR_ACCORDION_CARD_KIND: StoredCardKind = {
  templateType: TOUR_ACCORDION_TEMPLATE_TYPE,
  fieldType: TOUR_ACCORDION_FIELD_TYPE,
  listTypes: [TOUR_ACCORDION_TYPE],
  settings: TOUR_ACCORDION_FIELD_SETTINGS,
  max: MAX_TOUR_ACCORDION_TOURS,
  rootAttributesOf: (index, list, card) => ({
    name: tourAccordionGroupName(list, card),
    open: index === 0 ? true : undefined,
  }),
};
